import { IWIZComponent } from '../interfaces/iwiz-component';


/**
 * Base class for Wizeoo component events
 */
export class WizEvent {

  private _sourceEvent: CustomEvent<WizEvent>;

  /**
   * Reference to the CustomEvent that originally held the WizEvent.
   * Reverse composition
   */
  public set sourceEvent(pValue: CustomEvent<WizEvent>) {
    this._sourceEvent = pValue;
  }

  /** Event type code */
  private _type: string;

  public get type(): string {
    return this._type;
  }

  /** Component */
  private _component: IWIZComponent;

  public get component(): IWIZComponent {
    return this._component;
  }

  /** Value */
  private _value: any = undefined;

  public get value(): any {
    return this._value;
  }

  public set value(pValue: any) {
    this._value = pValue;
  }

  /**
   * Constructor
   * @param pType Event type code
   * @param pComponent Component
   */
  constructor(pType: string, pComponent?: IWIZComponent,  pValue?: any) {
    this._type = pType;
    if (pComponent) {
      this._component = pComponent;
    }
    if (pValue !== undefined) {
      this._value = pValue;
    }
  }

  /**
   * Calls the prevent default operation on the sourceEvent
   */
  public preventDefault(): void {
    if (!this._sourceEvent) {
      console.warn('Could not prevent default the Event as it does not have the source CustomEvent');
    }

    this._sourceEvent.preventDefault();
  }

  /**
   * Calls the stopImmediatePropagation on the sourceEvent
   */
  public stopImmediatePropagation(): void {
    if (!this._sourceEvent) {
      console.warn('Could not stopImmediatePropagation for the Event as it does not have the source CustomEvent');
    }

    this._sourceEvent.stopImmediatePropagation();
  }

  /**
   * Calls the stopImmediatePropagation on the sourceEvent
   */
  public stopPropagation(): void {
    if (!this._sourceEvent) {
      console.warn('Could not stopPropagation for the Event as it does not have the source CustomEvent');
    }

    this._sourceEvent.stopPropagation();
  }

  /**
   * A reference to the target to which the event was originally dispatched.
   */
  public get target(): EventTarget {
    if (!this._sourceEvent) {
      console.warn('Could not get target for the Event as it does not have the source CustomEvent');
    }
    return this._sourceEvent.target;
  }

  /**
   * A reference to the currently registered target for the event.
   * This is the object to which the event is currently slated to be sent.
   * It's possible this has been changed along the way through retargeting.
   */
  public get currentTarget(): EventTarget {
    if (!this._sourceEvent) {
      console.warn('Could not get currentTarget for the Event as it does not have the source CustomEvent');
    }
    return this._sourceEvent.currentTarget;
  }

  /**
   * Indicates whether or not the call to event.preventDefault() canceled the event.
   */
  public get defaultPrevented() {
    if (!this._sourceEvent) {
      console.warn('Could not get defaultPrevented for the Event as it does not have the source CustomEvent');
    }
    return this._sourceEvent.defaultPrevented;
  }

  /**
   * A boolean indicating whether or not the event bubbles up through the DOM.
   */
  public get bubbles() {
    if (!this._sourceEvent) {
      console.warn('Could not get bubbles for the Event as it does not have the source CustomEvent');
    }
    return this._sourceEvent.bubbles;
  }

  public clone(): WizEvent {
    const result = new WizEvent(this._type, this._component, this._value);

    return result;
  }
}
